import React, { useEffect, useRef } from "react";
import {
  DEFAULT_CANVAS_FONT_STYLE,
  DEFAULT_CANVAS_FONT_WEIGHT,
  getCanvasFontStack,
} from "../../../features/canvas/canvasFonts";
import { CANVAS_ELEMENT_TYPES } from "../../../features/canvas/nativeCanvasElements";
import { getElementBounds } from "../../../features/canvas/geometry/elementBounds";
import { getShapeLabelLayout } from "../../../features/canvas/geometry/shapeTextLayout";
import { getScreenBounds } from "../../../features/canvas/geometry/selectionHandles";
import {
  readCanvasThemePalette,
  resolveShapeFillColor,
  resolveShapeLabelColor,
  resolveTextColor,
} from "../../../features/canvas/renderer/canvasTheme";

const DEFAULT_FONT_SIZE = 20;
const DEFAULT_SHAPE_FILL = "#111827";
const DEFAULT_TEXT_FILL = "#f8fafc";
const EDITOR_MIN_HEIGHT = 28;
const EDITOR_MIN_WIDTH = 120;
const TEXT_LINE_HEIGHT_FACTOR = 1.3;
const TEXT_WIDTH_FACTOR = 0.6;

const stopPointerPropagation = (event) => event.stopPropagation();

const getEditorFontSize = ({ element, viewport }) =>
  (element.style?.fontSize ?? element.fontSize ?? DEFAULT_FONT_SIZE) *
  viewport.zoom;

const getTypedValueSize = ({ fontSize, value }) => {
  const lines = String(value ?? "").split("\n");
  const longestLineLength = Math.max(...lines.map((line) => line.length), 1);

  return {
    height: lines.length * fontSize * TEXT_LINE_HEIGHT_FACTOR,
    width: longestLineLength * fontSize * TEXT_WIDTH_FACTOR + fontSize,
  };
};

const getEditorFrame = ({ editingValue, element, fontSize, viewport }) => {
  const screenBounds = getScreenBounds({
    bounds: getElementBounds(element),
    viewport,
  });
  const typedSize = getTypedValueSize({ fontSize, value: editingValue });
  const isLabelEditor = element.type !== CANVAS_ELEMENT_TYPES.TEXT;
  if (isLabelEditor) {
    const layout = getShapeLabelLayout({ element, label: editingValue });
    return {
      height: Math.max(screenBounds.height, layout.requiredHeight * viewport.zoom),
      left: screenBounds.x,
      top: screenBounds.y,
      width: screenBounds.width,
    };
  }

  return {
    height: Math.max(screenBounds.height, typedSize.height, EDITOR_MIN_HEIGHT),
    left: screenBounds.x,
    top: screenBounds.y,
    width: Math.max(screenBounds.width, typedSize.width, EDITOR_MIN_WIDTH),
  };
};

const getLabelCenteringStyle = ({ editingValue, element, frame, viewport }) => {
  const layout = getShapeLabelLayout({ element, label: editingValue });
  const textHeight = layout.lines.length * layout.lineHeight * viewport.zoom;
  return {
    paddingLeft: layout.horizontalPadding * viewport.zoom,
    paddingRight: layout.horizontalPadding * viewport.zoom,
    paddingTop: Math.max((frame.height - textHeight) / 2, 0),
    textAlign: "center",
  };
};

const getEditorStyle = ({
  editingValue,
  element,
  fontSize,
  frame,
  palette,
  viewport,
}) => {
  const isLabelEditor = element.type !== CANVAS_ELEMENT_TYPES.TEXT;
  return {
    color: getEditorTextColor({ element, palette }),
    fontFamily: getCanvasFontStack(element.style?.fontFamily),
    fontSize,
    fontStyle: element.style?.fontStyle ?? DEFAULT_CANVAS_FONT_STYLE,
    fontWeight: element.style?.fontWeight ?? DEFAULT_CANVAS_FONT_WEIGHT,
    height: frame.height,
    left: frame.left,
    lineHeight: TEXT_LINE_HEIGHT_FACTOR,
    top: frame.top,
    width: frame.width,
    ...(isLabelEditor
      ? getLabelCenteringStyle({ editingValue, element, frame, viewport })
      : {}),
  };
};

const handleEditorKeyDown = ({ event, onCancel, onCommit }) => {
  if (event.key === "Escape") onCancel(event);
  if (event.key === "Enter" && !event.shiftKey) onCommit(event);
};

const getEditorTextColor = ({ element, palette }) => {
  if (element.type === CANVAS_ELEMENT_TYPES.TEXT) {
    return resolveTextColor({
      color: element.style?.fill ?? DEFAULT_TEXT_FILL,
      palette,
    });
  }
  const fill = resolveShapeFillColor({
    color: element.style?.fill ?? DEFAULT_SHAPE_FILL,
    palette,
  });
  return resolveShapeLabelColor({
    color: element.style?.color ?? DEFAULT_TEXT_FILL,
    fill,
    palette,
  });
};

const CanvasTextArea = ({
  editingValue, editorRef, element, fontSize, frame,
  onCancel, onChange, onCommit, viewport,
}) => (
  <textarea
    aria-label="Edit canvas text"
    className="canvas-text-editor absolute z-20"
    onBlur={onCommit}
    onChange={(event) => onChange(event.target.value)}
    onKeyDown={(event) => handleEditorKeyDown({ event, onCancel, onCommit })}
    onPointerDown={stopPointerPropagation}
    ref={editorRef}
    style={getEditorStyle({
      editingValue,
      element,
      fontSize,
      frame,
      palette: readCanvasThemePalette(),
      viewport,
    })}
    value={editingValue}
    wrap="soft"
  />
);

/**
 * Chromeless screen-space textarea for editing shape labels and text
 * elements — no border or background, just the caret and text, sized and
 * positioned to match how the committed text renders on canvas. The canvas
 * hides the edited element's text while this overlay is open.
 * @param {{ editingElementId: string | null, editingValue: string, engine: object, onCancel: Function, onChange: Function, onCommit: Function }} props
 * @returns {JSX.Element | null}
 */
const CanvasTextEditorOverlay = ({
  editingElementId, editingValue, engine, onCancel, onChange, onCommit,
}) => {
  const editorRef = useRef(null);

  useEffect(() => {
    if (!editingElementId) return;
    editorRef.current?.focus();
    editorRef.current?.select();
  }, [editingElementId]);

  if (!editingElementId) return null;
  const element = engine.getEditingElement();
  if (!element) return null;

  const viewport = engine.getState().viewport;
  const fontSize = getEditorFontSize({ element, viewport });
  const frame = getEditorFrame({ editingValue, element, fontSize, viewport });
  return (
    <CanvasTextArea
      editingValue={editingValue}
      editorRef={editorRef}
      element={element}
      fontSize={fontSize}
      frame={frame}
      onCancel={onCancel}
      onChange={onChange}
      onCommit={onCommit}
      viewport={viewport}
    />
  );
};

export default CanvasTextEditorOverlay;
